import { parseCheckoutNext, type CheckoutChoice } from '@lyrmo/shared';
import { en, type Messages } from '../../i18n/en';
import { appViewPaths } from '../../layout/useAppView';
import { TRANSACTION_PARAM } from './paddle-checkout';
import type { AccountDialog, Notice } from './account-context';

/**
 * What the address says on arrival: the API's redirects report a sign-in link, checkout, or a
 * changed payment method, and links from elsewhere, such as the desktop app, can open a
 * dialog.
 */
export interface Arrival {
  readonly notice: Notice | null;
  /** Continue to checkout for this plan and billing interval, chosen before signing in. */
  readonly checkout: CheckoutChoice | null;
  /** The plan may have just changed; the payment provider's webhook can land after checkout. */
  readonly planChanged: boolean;
  /** A dialog to open straight away, such as from `?dialog=plans`. */
  readonly dialog: AccountDialog | null;
}

/** Query parameters the API adds to its redirects, and ?dialog. */
const ARRIVAL_PARAMS = ['signed_in', 'sign_in', 'continue', 'checkout', 'billing', 'dialog'];

const LINKABLE_DIALOGS: readonly AccountDialog[] = ['plans', 'sign-in', 'tones', 'api-keys'];
// Older releases of the desktop app link to the style guide that tones replaced.
const RENAMED_DIALOGS: Readonly<Record<string, AccountDialog>> = { 'style-guide': 'tones' };

function arrivalNotice(
  params: URLSearchParams,
  checkout: CheckoutChoice | null,
  t: Messages,
): Notice | null {
  switch (params.get('checkout')) {
    case 'success':
      return { tone: 'success', text: t.notices.subscribed };
    case 'cancelled':
      return { tone: 'warning', text: t.notices.checkoutCancelled };
  }
  if (params.get('billing') === 'updated') {
    return { tone: 'success', text: t.notices.billingUpdated };
  }
  switch (params.get('sign_in')) {
    case 'expired':
      return {
        tone: 'warning',
        text: t.notices.linkExpired,
        action: { label: t.notices.getNewLink, dialog: 'sign-in' },
      };
    case 'required':
      return {
        tone: 'warning',
        text: t.notices.signInToSubscribe,
        action: { label: t.common.signIn, dialog: 'sign-in' },
      };
  }
  if (params.get('signed_in') === '1') {
    return {
      tone: 'success',
      text: checkout ? t.signIn.signedInCheckout : t.signIn.signedIn,
    };
  }
  return null;
}

/** What the address says, with any notice in the interface's language. */
export function readArrival(search: string, t: Messages = en): Arrival {
  const params = new URLSearchParams(search);
  const checkout = parseCheckoutNext(params.get('continue'));
  const asked = params.get('dialog');
  const dialog = (asked && RENAMED_DIALOGS[asked]) ?? asked;
  return {
    notice: arrivalNotice(params, checkout, t),
    checkout,
    planChanged: params.get('checkout') === 'success' || params.get('billing') === 'updated',
    dialog: LINKABLE_DIALOGS.find((linkable) => linkable === dialog) ?? null,
  };
}

/**
 * Opens the studio for the API's redirects, links to a dialog, and Paddle's checkout links, which
 * all come to the site's first address: what they finish or open is the writer's, and older
 * emails and desktop apps keep linking there.
 */
export function arriveInStudio(): void {
  const url = new URL(window.location.href);
  if (url.pathname !== appViewPaths.home) return;
  if (![...ARRIVAL_PARAMS, TRANSACTION_PARAM].some((param) => url.searchParams.has(param))) return;
  url.pathname = appViewPaths.write;
  window.history.replaceState(window.history.state, '', url);
}

/** Removes the arrival parameters from the address bar. False when there were none. */
export function clearArrival(): boolean {
  const url = new URL(window.location.href);
  if (!ARRIVAL_PARAMS.some((param) => url.searchParams.has(param))) return false;
  for (const param of ARRIVAL_PARAMS) url.searchParams.delete(param);
  window.history.replaceState(window.history.state, '', url);
  return true;
}
